import { musicPlatform } from 'fitbeat.gym-core/config';
import { createSelector } from 'reselect';
import { IAppStore } from '../../reducer';
import { MusicInitialiseStates } from './interfaces';
import { truncate } from './SongListItem';

export const getMusicPlayerToAutoplaySelector = createSelector(
  [(state: IAppStore) => state.music.musicPlayerToAutoplay],
  (musicPlayerToAutoplay) => musicPlayerToAutoplay,
);

const getMusicPlayerState = (state: IAppStore) => state.music.playerState;

export const getMusicPlayerStateSelector = createSelector(
  [getMusicPlayerState],
  (playerState) => playerState,
);

const getConnectedState = (state: IAppStore) => state.music.isConnected;

export const getConnectedStateSelector = createSelector(
  [getConnectedState],
  (isConnected) => isConnected,
);

const getSongsLoadingState = (state: IAppStore) => state.music.songsLoading;

export const getSongsLoadingStateSelector = createSelector(
  [getSongsLoadingState],
  (songsLoading) => songsLoading,
);

const getPlaylistsLoadingState = (state: IAppStore) => state.music.playlistsLoading;

export const getPlaylistLoadingStateSelector = createSelector(
  [getPlaylistsLoadingState],
  (playlistsLoading) => playlistsLoading,
);

const getSongs = (state: IAppStore) => state.music.songs;

const getPlayingSong = (state: IAppStore) => {
  if (state.music.playerState && state.music.playerState.currentSong) {
    const currentSong = state.music.playerState!.currentSong;
    const playingSongFromPlaylist =
      state.music.songs.find((song) => song.title === currentSong.title && song.artist === currentSong.artist );
    if (playingSongFromPlaylist) {
      return playingSongFromPlaylist;
    }
    return state.music.playerState.currentSong;
  }
};

export const getSongsSelector = createSelector(
  [getSongs, getMusicPlayerState],
  (songs, playerState) => {
    return songs.map((song) => {
      const songCopy = Object.assign({}, song);
      songCopy.isPlaying = (playerState && !playerState.isPaused && playerState.currentSong.uri === song.uri);
      return songCopy;
    });
  },
);

export const getPlayingSongSelector = createSelector(
  [getPlayingSong],
  (playingSong) => playingSong,
);

const getPlaylists = (state: IAppStore) => [
  ...state.music.fbPlaylists,
  ...state.music.recommendedPlaylists,
  ...state.music.userPlaylists,
];

export const getPlaylistsSelector = createSelector(
  [getPlaylists],
  (playlists) => playlists,
);

const getError = (state: IAppStore) => state.music.error;

export const getErrorSelector = createSelector(
  [getError],
  (error) => error,
);

const getPremiumUser = (state: IAppStore) => state.music.premiumUser;

export const getPremiumUserSelector = createSelector(
  [getPremiumUser],
  (premiumUser) => premiumUser,
);

const getSelectedPlaylist = (state: IAppStore) => state.music.selectedPlaylist;

export const getSelectedPlaylistSelector = createSelector(
  [getSelectedPlaylist],
  (selectedPlaylist) => selectedPlaylist,
);

const getSelectedMusicPlayer = (state: IAppStore) => state.music.musicPlayer;
const getSpotifyDisplayName = (state: IAppStore) => state.music.displayName;

export const getSelectedMusicPlayerSelector = createSelector(
  [getSelectedMusicPlayer],
  (musicPlayer) => musicPlayer,
);

const getAuthenticationFailed = (state: IAppStore) => state.music.authenticationFailed;

export const getAuthenticationFailedSelector = createSelector(
  [getAuthenticationFailed],
  (authenticationFailed) => authenticationFailed,
);

const getAuthenticationFailedMessage = (state: IAppStore) => state.music.authenticationFailMessage;

export const authenticationFailedMessageSelector = createSelector(
  [getAuthenticationFailedMessage],
  (authenticationFailMessage) => authenticationFailMessage,
);

export const didMusicPlayerToAutoplayFailSelector = createSelector(
  [
    (state: IAppStore) => state.music.authenticationFailed,
    (state: IAppStore) => state.music.authenticationFailedPlayer,
    (state: IAppStore) => state.music.musicPlayerToAutoplay,
  ],
  (authenticationFailed, authenticationFailedPlayer, musicPlayerToAutoplay) => {
    return authenticationFailed && authenticationFailedPlayer === musicPlayerToAutoplay;
  },
);

const getCurrentGenericMusicTrackNumber = (state: IAppStore) => state.music.currentGenericMusicTrackIndex + 1;

export const nowPlayingTextSelector = createSelector(
  [getSelectedMusicPlayer, getPlayingSong, getCurrentGenericMusicTrackNumber],
  (musicPlayer, playingSong, currentGenericMusicTrackNumber) => {
    if (musicPlayer === musicPlatform.AppleMusic || musicPlayer === musicPlatform.Spotify) {
      if (!playingSong || !playingSong.title || playingSong.title === '--') {
        return musicPlayer;
      }
      return truncate(playingSong!.title, 25);
    }
    const genericMusicPlaylistText = currentGenericMusicTrackNumber ?
      `Fitillion Power - Track ${currentGenericMusicTrackNumber}` :
      'Fitillion Power Playlist';
    return genericMusicPlaylistText;
  },
);

const getCurrentPlayingTrackIndex = (state: IAppStore) => state.music.currentGenericMusicTrackIndex;
const getGenericMusicPlaylist = (state: IAppStore) => state.music.genericMusicPlaylist;

export const currentPlayingTrackUrlSelector = createSelector(
  [getCurrentPlayingTrackIndex, getGenericMusicPlaylist],
  (currentPlayingTrackIndex, genericMusicPlaylist) =>
      genericMusicPlaylist[currentPlayingTrackIndex] &&
      genericMusicPlaylist[currentPlayingTrackIndex].url);

const getGenericMusicIsPaused = (state: IAppStore) => state.music.genericMusicIsPaused;

export const genericMusicIsPausedSelector = createSelector(
  [getGenericMusicIsPaused],
  (genericMusicIsPaused) => genericMusicIsPaused);

const getGenericMusicVolume = (state: IAppStore) => state.homeWorkouts.genericMusicVolume;

export const genericMusicVolumeSelector = createSelector(
  [getGenericMusicVolume],
  (genericMusicVolume) => genericMusicVolume);

const getDisplayMusicScreen = (state: IAppStore) => state.music.displayMusicScreen;

export const displayMusicScreenSelector = createSelector(
  [getDisplayMusicScreen],
  (displayMusicScreen) => displayMusicScreen,
);

export const hasSetupMusicSelector = createSelector(
  [(state: IAppStore) => state.music.musicInitialiseState,
   (state: IAppStore) => state.music.musicPlayer],
  (musicInitialiseState: MusicInitialiseStates, musicPlayer: musicPlatform | null) =>
    musicInitialiseState === 'done'
    && (musicPlayer === musicPlatform.Spotify || musicPlayer === musicPlatform.AppleMusic),
);

export const spotifyDisplayNameSelector = createSelector(
  [getSpotifyDisplayName],
  (displayName) => displayName,
);
